/**
 * FloatingNav — super-app style pill nav for mobile.
 *
 * Layout (bottom-center, fixed, safe-area aware):
 *
 *   ┌──────────────────────────────────────────┐
 *   │  [WS]  Home  Library  ✻FAB  Brand  More │
 *   └──────────────────────────────────────────┘
 *
 * Interaction model:
 *   - The workspace chip opens a bottom sheet to switch teams.
 *   - The four destinations animate their label pill on active.
 *   - The centre FAB launches dashboard with a fresh prompt ready.
 *   - The More item opens a second sheet with overflow destinations.
 *   - Every tap fires a 10ms haptic pulse on supporting devices.
 *
 * Visibility: hidden at >=1024px (desktop TopNav takes over).
 */

import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  Image as ImageIcon,
  Briefcase,
  MoreHorizontal,
  Sparkles,
  ChevronUp,
} from "lucide-react";

import { useWorkspace } from "@/hooks/useWorkspace";
import { useAuth } from "@/hooks/useAuth";
import { ROUTES, type AppRoute } from "@/types";
import { initials } from "@/utils/format";
import { optimizedUrl, optimizedSrcSet } from "@/lib/files";
import WorkspaceSwitcherSheet from "./WorkspaceSwitcherSheet";
import MobileMoreSheet from "./MobileMoreSheet";

const DESTINATIONS: {
  to: AppRoute;
  label: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
}[] = [
  { to: ROUTES.DASHBOARD, label: "Home",    icon: LayoutGrid },
  { to: ROUTES.LIBRARY,   label: "Library", icon: ImageIcon },
  { to: ROUTES.BRAND_KIT, label: "Brand",   icon: Briefcase },
];

/** Fires a short haptic if the device supports it. No-op on desktop. */
function haptic(ms = 10) {
  try { if ("vibrate" in navigator) navigator.vibrate(ms); } catch { /* ignore */ }
}

const FloatingNav: React.FC = () => {
  const { pathname } = useLocation();
  const navigate     = useNavigate();
  const { active }   = useWorkspace();
  const { logout }   = useAuth();
  const [wsOpen,   setWsOpen]   = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const activeIndex = useMemo(() => {
    return DESTINATIONS.findIndex(
      (d) => pathname === d.to || (d.to !== ROUTES.DASHBOARD && pathname.startsWith(d.to)),
    );
  }, [pathname]);

  const logoSrc = active?.logo ? optimizedUrl(active, active.logo, { w: 64, q: 82 }) : "";
  const logoSrcSet = active?.logo ? optimizedSrcSet(active, active.logo, 32, { q: 82 }) : "";

  const onFab = () => {
    haptic(14);
    navigate(`${ROUTES.DASHBOARD}?new=1`);
  };

  return (
    <>
      <nav
        className="cl-floating-nav"
        aria-label="Primary"
        style={{
          position: "fixed",
          left: "50%",
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 14px)",
          transform: "translateX(-50%)",
          zIndex: 60,
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "6px",
          background: "color-mix(in srgb, var(--bg-surface) 72%, transparent)",
          border: "1px solid var(--border-default)",
          borderRadius: 999,
          backdropFilter: "blur(16px) saturate(160%)",
          WebkitBackdropFilter: "blur(16px) saturate(160%)",
          boxShadow:
            "0 18px 36px -16px rgba(0,0,0,0.22), 0 4px 12px -4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.5)",
          minWidth: 320,
          maxWidth: "calc(100vw - 24px)",
        }}
      >
        {/* ── Workspace chip ───────────────────────────────── */}
        <button
          onClick={() => { haptic(); setWsOpen(true); }}
          aria-label={active ? `Switch from ${active.name}` : "Pick a workspace"}
          style={{
            position: "relative",
            width: 40, height: 40,
            borderRadius: "50%",
            border: "1.5px solid var(--border-strong, var(--border-default))",
            background: "var(--bg-surface-alt)",
            padding: 0,
            overflow: "hidden",
            display: "inline-flex",
            alignItems: "center", justifyContent: "center",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          {active?.logo ? (
            <img
              src={logoSrc}
              srcSet={logoSrcSet}
              alt=""
              decoding="async"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <span style={{
              fontSize: 11, fontWeight: 700,
              color: "var(--text-tertiary)",
              fontFamily: "var(--font-display)",
            }}>
              {initials(active?.name ?? "?")}
            </span>
          )}
          {/* tiny chevron hint that this is interactive */}
          <span style={{
            position: "absolute",
            right: -2, bottom: -2,
            width: 14, height: 14,
            borderRadius: "50%",
            background: "var(--brand-gradient)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
            color: "#fff",
          }}>
            <ChevronUp size={8} strokeWidth={3} />
          </span>
        </button>

        {/* ── Left two destinations (before the FAB) ──────── */}
        {DESTINATIONS.slice(0, 2).map((d, i) => (
          <NavItem
            key={d.to}
            to={d.to}
            label={d.label}
            Icon={d.icon}
            active={activeIndex === i}
          />
        ))}

        {/* ── Centre FAB ──────────────────────────────────── */}
        <button
          onClick={onFab}
          aria-label="Start a new generation"
          style={{
            width: 52, height: 52,
            borderRadius: "50%",
            border: "none",
            background: "var(--brand-gradient)",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center", justifyContent: "center",
            boxShadow:
              "0 10px 24px -6px rgba(197,63,255,0.45), 0 4px 10px -3px rgba(255,107,157,0.35), inset 0 1px 0 rgba(255,255,255,0.35)",
            cursor: "pointer",
            flexShrink: 0,
            transform: "translateY(-6px)",
            transition: "transform 200ms, box-shadow 200ms",
          }}
          onPointerDown={(e) => {
            (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-4px) scale(0.94)";
          }}
          onPointerUp={(e) => {
            (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-6px)";
          }}
          onPointerLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-6px)";
          }}
        >
          <Sparkles size={22} strokeWidth={2} />
        </button>

        {/* ── Right destination ───────────────────────────── */}
        {DESTINATIONS.slice(2).map((d, i) => (
          <NavItem
            key={d.to}
            to={d.to}
            label={d.label}
            Icon={d.icon}
            active={activeIndex === i + 2}
          />
        ))}

        {/* ── More ────────────────────────────────────────── */}
        <button
          onClick={() => { haptic(); setMoreOpen(true); }}
          aria-label="More"
          style={{
            display: "inline-flex",
            alignItems: "center", justifyContent: "center",
            width: 40, height: 40,
            borderRadius: "50%",
            background: "transparent",
            border: "none",
            color: "var(--text-secondary)",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <MoreHorizontal size={18} strokeWidth={1.75} />
        </button>
      </nav>

      {/* Sheets */}
      {wsOpen && <WorkspaceSwitcherSheet onClose={() => setWsOpen(false)} />}
      {moreOpen && (
        <MobileMoreSheet
          onClose={() => setMoreOpen(false)}
          onLogout={() => { setMoreOpen(false); logout(); }}
        />
      )}

      <style>{`
        @media (min-width: 1024px) {
          .cl-floating-nav { display: none !important; }
        }
      `}</style>
    </>
  );
};

/** Individual destination — icon collapses to icon+label pill when active. */
const NavItem: React.FC<{
  to: string;
  label: string;
  Icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  active: boolean;
}> = ({ to, label, Icon, active }) => (
  <Link
    to={to}
    onClick={() => haptic()}
    aria-label={label}
    aria-current={active ? "page" : undefined}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      height: 40,
      padding: active ? "0 14px 0 12px" : "0",
      width: active ? "auto" : 40,
      borderRadius: 999,
      background: active
        ? "color-mix(in srgb, var(--accent-300) 14%, transparent)"
        : "transparent",
      color: active ? "var(--text-primary)" : "var(--text-secondary)",
      fontSize: "var(--font-size-xs)",
      fontWeight: 600,
      textDecoration: "none",
      transition: "all 260ms cubic-bezier(0.4, 0, 0.2, 1)",
      flexShrink: 0,
      overflow: "hidden",
      whiteSpace: "nowrap",
    }}
  >
    <Icon size={18} strokeWidth={active ? 2 : 1.5} />
    {active && <span>{label}</span>}
  </Link>
);

export default FloatingNav;
